// Typed fetch wrappers for the Netlify /api/* proxy functions. The server-side
// Google/ORS keys never reach the browser — these all hit same-origin proxies.

import type { ApproxLocation, LatLng, Prediction } from './types';

export interface GeocodeResult {
  lat: number;
  lng: number;
  formattedAddress: string;
}

export interface NeighborhoodResult {
  name: string;
  source: string;
  formattedAddress?: string;
}

/** GET /api/maps-key → the referrer-restricted browser Maps JS key. */
export async function fetchMapsKey(): Promise<string | null> {
  try {
    const resp = await fetch('/api/maps-key', { cache: 'no-store' });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data && data.key ? data.key : null;
  } catch {
    return null;
  }
}

/** GET /api/geocode?address=... → first result's location + formatted address. */
export async function geocode(address: string): Promise<GeocodeResult> {
  const resp = await fetch(`/api/geocode?address=${encodeURIComponent(address)}`);
  if (!resp.ok) throw new Error('Geocode failed: HTTP ' + resp.status);
  const data = await resp.json();
  if (data.status !== 'OK' || !data.results?.[0]) {
    throw new Error('Geocode failed: ' + (data.status || 'unknown'));
  }
  const r = data.results[0];
  return {
    lat: r.geometry.location.lat,
    lng: r.geometry.location.lng,
    formattedAddress: r.formatted_address,
  };
}

/** GET /api/geocode?latlng=... → formatted address, or "lat, lng" on failure. */
export async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const resp = await fetch(`/api/geocode?latlng=${lat},${lng}`);
    if (resp.ok) {
      const data = await resp.json();
      if (data.status === 'OK' && data.results?.[0]) {
        return data.results[0].formatted_address;
      }
    }
  } catch {
    /* fall through */
  }
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

/** GET /api/neighborhood?lat=...&lng=... -> best-effort named area label. */
export async function lookupNeighborhood(lat: number, lng: number): Promise<NeighborhoodResult | null> {
  try {
    const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
    const resp = await fetch(`/api/neighborhood?${params}`);
    if (!resp.ok) return null;
    const data = await resp.json();
    return data.status === 'OK' && data.neighborhood?.name ? data.neighborhood : null;
  } catch {
    return null;
  }
}

/** GET /api/geo -> approximate city/region from Netlify Edge geolocation. */
export async function fetchApproxLocation(): Promise<ApproxLocation | null> {
  try {
    const resp = await fetch('/api/geo', { cache: 'no-store' });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!Number.isFinite(data.lat) || !Number.isFinite(data.lng)) return null;
    return {
      lat: data.lat,
      lng: data.lng,
      city: data.city,
      region: data.region,
      country: data.country,
      timezone: data.timezone,
      source: 'edge',
    };
  } catch {
    return null;
  }
}

/** GET /api/places-autocomplete → predictions for a typeahead. */
export async function placesAutocomplete(
  input: string,
  opts: { sessiontoken?: string; location?: string; radius?: string } = {},
): Promise<Prediction[]> {
  const params = new URLSearchParams({ input });
  if (opts.sessiontoken) params.set('sessiontoken', opts.sessiontoken);
  if (opts.location) params.set('location', opts.location);
  if (opts.radius) params.set('radius', opts.radius);
  const resp = await fetch(`/api/places-autocomplete?${params.toString()}`);
  const data = await resp.json();
  return data.status === 'OK' && Array.isArray(data.predictions) ? data.predictions : [];
}

/** GET /api/places-nearby → nearest places of a type, ranked by distance. */
export async function placesNearby(
  center: LatLng,
  type: string,
): Promise<any[]> {
  const params = new URLSearchParams({
    location: `${center.lat},${center.lng}`,
    rankby: 'distance',
    type,
  });
  try {
    const resp = await fetch(`/api/places-nearby?${params}`);
    if (!resp.ok) return [];
    const data = await resp.json();
    if (data.status !== 'OK' || !data.results?.length) return [];
    return data.results;
  } catch {
    return [];
  }
}

export interface OsmPlace {
  id: string;
  name: string;
  /** False when OSM has no name and we fell back to the category label. */
  named: boolean;
  lat: number;
  lng: number;
  distanceM: number;
  areaSqm?: number;
}

/**
 * GET /api/osm-nearby → nearby OpenStreetMap features per category (parks with
 * polygon areas, tennis courts, pools, gyms, …), sorted by distance.
 * Returns null on upstream failure so callers can distinguish "nothing there"
 * from "Overpass was down".
 */
export async function osmNearby(
  center: LatLng,
  categories: string[],
  radius = 3000,
): Promise<Record<string, OsmPlace[]> | null> {
  if (!categories.length) return {};
  const params = new URLSearchParams({
    // ~100 m rounding so lookups near the same spot share a CDN cache key.
    location: `${center.lat.toFixed(3)},${center.lng.toFixed(3)}`,
    categories: [...new Set(categories)].sort().join(','),
    radius: String(radius),
  });
  try {
    const resp = await fetch(`/api/osm-nearby?${params}`);
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data.status !== 'OK' || !data.results) return null;
    return data.results;
  } catch {
    return null;
  }
}

// The isochrone overlay is handled in maps/overlays.ts (it POSTs to
// /api/isochrone and owns the non-reactive circle/polygon/cache).
